import { useEffect, useState, useRef } from "react";
import api, { API, getToken } from "@/lib/api";
import { usePlzAutocomplete, PlzAutocompleteHint } from "@/lib/usePlzAutocomplete";
import { CONTACT_ROLES, SALUTATIONS, contactDisplayName, formatDate, displayEmail, firstEmail, firstPhone, ADDRESS_LINE1_COMMON, suggestAddressLine1 } from "@/lib/constants";
import { useTableSort } from "@/lib/useTableSort";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, MagnifyingGlass, Trash, PencilSimple, PaperPlaneTilt, FilePdf, ShieldCheck, FolderOpen, UploadSimple, Buildings } from "@phosphor-icons/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import EmailListField from "@/components/EmailListField";
import PhoneListField from "@/components/PhoneListField";
import CompanyContactPersonsField from "@/components/CompanyContactPersonsField";
import MessagingDialog from "@/components/MessagingDialog";
import OwnerPortalDialog from "@/components/OwnerPortalDialog";

const ROLE_OPTIONS = Object.entries(CONTACT_ROLES);

export default function Contacts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msgContact, setMsgContact] = useState(null);
  const [portalContact, setPortalContact] = useState(null);
  // 2026-02 (iter120) — URL-Params: /kontakte?role=external_weg&new=1 öffnet
  // den Anlage-Dialog mit vorausgewählter Rolle (z. B. Fremd-WEG anlegen).
  const [searchParams, setSearchParams] = useSearchParams();
  const [presetRole, setPresetRole] = useState(null);
  const [presetContactType, setPresetContactType] = useState(null);
  useEffect(() => {
    const legacyId = searchParams.get("id");
    if (legacyId) { navigate(`/kontakte/${legacyId}`, { replace: true }); return; }
    const r = searchParams.get("role");
    const isNew = searchParams.get("new");
    if (r && CONTACT_ROLES[r]) {
      setRoleFilter(r);
      if (isNew === "1" && !open) {
        setPresetRole(r);
        // Fremd-verwaltete WEG ist per Definition ein Unternehmen/Institution
        setPresetContactType(r === "external_weg" ? "company" : null);
        setEditing(null);
        setOpen(true);
        // Params entfernen, damit ein Reload nicht erneut öffnet
        const sp = new URLSearchParams(searchParams);
        sp.delete("new");
        setSearchParams(sp, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const params = { limit: 500 };
    if (q) params.q = q;
    if (roleFilter !== "all") params.role = roleFilter;
    const { data } = await api.get("/contacts", { params });
    setItems(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, roleFilter]);

  const onSaved = () => { setOpen(false); setEditing(null); load(); };

  const { sorted, SortableTh } = useTableSort(items, "name", "asc", {
    name: contactDisplayName,
    type: (c) => c.contact_type,
    email: (c) => firstEmail(c.emails),
    phone: (c) => firstPhone(c.phones),
    city: (c) => c.address?.city || "",
    owner_number: (c) => c.owner_number || "",
  });

  return (
    <div className="space-y-4" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Kontakte</h1>
          <p className="text-sm text-slate-500 mt-1">Eigentümer, Mieter, Lieferanten und mehr</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <div className="flex items-center gap-2">
            {/* iter138 — Multi-VCF-Import */}
            <VcfImportButton onDone={load} />
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="new-contact-btn">
                <Plus size={16} /> Neuer Kontakt
              </Button>
            </DialogTrigger>
          </div>
          {/* 2026-08 — key erzwingt Remount pro Öffnung → verhindert State-Leak
              (Reste vom vorherigen Kontakt bleiben nicht mehr im Formular). */}
          <ContactDialog
            key={open ? (editing?.id || `new-${presetRole || "any"}`) : "closed"}
            editing={editing}
            onSaved={onSaved}
            initialRole={editing ? null : presetRole}
            initialContactType={editing ? null : presetContactType}
          />
        </Dialog>
      </div>

      {/* Menüband mit Rollen-Tabs */}
      <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto" data-testid="contact-role-tabs">
        <TabButton active={roleFilter === "all"} onClick={() => setRoleFilter("all")} testid="role-tab-all">Alle</TabButton>
        {ROLE_OPTIONS.map(([k, v]) => (
          <TabButton key={k} active={roleFilter === k} onClick={() => setRoleFilter(k)} testid={`role-tab-${k}`}>{v}</TabButton>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Suchen nach Name, Firma, E-Mail..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="contact-search"
          />
        </div>
        <div className="text-xs text-slate-500 ml-auto">{items.length} Kontakt(e)</div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Mobile-Karten */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="contacts-mobile-list">
          {sorted.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Keine Kontakte gefunden.</div>}
          {sorted.map((c) => (
            <div key={c.id} className="p-3 active:bg-slate-50" data-testid={`contact-card-${c.id}`}>
              <Link
                to={`/kontakte/${c.id}`}
                className="block w-full text-left"
                data-testid={`contact-open-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-medium text-slate-900 text-sm">{contactDisplayName(c)}</div>
                  {c.owner_number && <div className="font-mono text-xs text-slate-500">{c.owner_number}</div>}
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(c.roles || []).slice(0, 4).map((r) => (
                    <Badge key={r} variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
                      {CONTACT_ROLES[r] || r}
                    </Badge>
                  ))}
                </div>
                <div className="text-xs text-slate-600 space-y-0.5">
                  {firstEmail(c.emails) && <div className="truncate">{firstEmail(c.emails)}</div>}
                  {firstPhone(c.phones) && <div className="tabular-nums">{firstPhone(c.phones)}</div>}
                  {c.address?.city && <div>{c.address.city}</div>}
                </div>
              </Link>
              <div className="mt-2 flex items-center gap-1 justify-end">
                <Button
                  size="icon" variant="ghost" className="h-7 w-7 hover:text-slate-900"
                  onClick={(e) => { e.stopPropagation(); setMsgContact(c); }}
                  title="Schreiben verfassen"
                  data-testid={`write-contact-mobile-${c.id}`}
                >
                  <PaperPlaneTilt size={14} />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!await window._domivusConfirm("Kontakt wirklich löschen?")) return;
                    await api.delete(`/contacts/${c.id}`);
                    toast.success("Kontakt gelöscht");
                    load();
                  }}
                  data-testid={`delete-contact-mobile-${c.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-slate-700 text-left">
            <tr>
              <SortableTh field="name">Name</SortableTh>
              <SortableTh field="owner_number">Eigent.-Nr.</SortableTh>
              <SortableTh field="type">Typ</SortableTh>
              <Th>Rollen</Th>
              <SortableTh field="email">E-Mail</SortableTh>
              <SortableTh field="phone">Telefon</SortableTh>
              <SortableTh field="city">Stadt</SortableTh>
              <Th className="w-24 text-right">Aktion</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">Keine Kontakte gefunden.</td></tr>
            )}
            {sorted.map((c) => (
              <tr
                key={c.id}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                data-testid={`contact-row-${c.id}`}
                onClick={() => navigate(`/kontakte/${c.id}`)}
              >
                <Td className="font-medium text-slate-900">
                  <Link
                    to={`/kontakte/${c.id}`}
                    className="hover:text-teal-700"
                    data-testid={`contact-open-${c.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {contactDisplayName(c)}
                  </Link>
                </Td>
                <Td className="font-mono text-slate-700">{c.owner_number || "—"}</Td>
                <Td>{{
                  person: "Person", company: "Firma", institution: "Institution",
                  court: "Gericht", authority_office: "Behörde",
                }[c.contact_type] || c.contact_type}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {(c.roles || []).slice(0, 3).map((r) => (
                      <Badge key={r} variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
                        {CONTACT_ROLES[r] || r}
                      </Badge>
                    ))}
                  </div>
                </Td>
                <Td className="text-slate-700">{firstEmail(c.emails) || "—"}</Td>
                <Td className="text-slate-700 tabular-nums">{firstPhone(c.phones) || "—"}</Td>
                <Td>{c.address?.city || "—"}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {(c.roles || []).includes("owner") && (
                      <Link
                        to={`/eigentuemer/${c.id}/mappe`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:text-indigo-700 hover:bg-slate-100"
                        title="Dokumentenmappe des Eigentümers"
                        data-testid={`owner-mappe-${c.id}`}
                      >
                        <FolderOpen size={14} />
                      </Link>
                    )}
                    {(c.roles || []).includes("owner") && (
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7 hover:text-[#0F2A4D]"
                        onClick={() => setPortalContact(c)}
                        title="Eigentümerportal verwalten"
                        data-testid={`owner-portal-${c.id}`}
                      >
                        <ShieldCheck size={14} />
                      </Button>
                    )}
                    {(c.roles || []).includes("owner") && (
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7 hover:text-emerald-700"
                        onClick={async () => {
                          try {
                            const r = await fetch(`${API}/owners/${c.id}/report-pdf`, {
                              headers: { Authorization: `Bearer ${getToken()}` },
                            });
                            if (!r.ok) throw new Error("Report konnte nicht erzeugt werden");
                            const blob = await r.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `Eigentuemerreport_${c.owner_number || c.id.slice(0, 6)}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success("Report heruntergeladen");
                          } catch (err) { toast.error(err.message); }
                        }}
                        title="Eigentümerreport (PDF)"
                        data-testid={`owner-report-${c.id}`}
                      >
                        <FilePdf size={14} />
                      </Button>
                    )}
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7 hover:text-slate-900"
                      onClick={() => setMsgContact(c)}
                      title="Schreiben verfassen"
                      data-testid={`write-contact-${c.id}`}
                    >
                      <PaperPlaneTilt size={14} />
                    </Button>
                    {c.roles?.includes("owner") && c.owner_number && (
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7 hover:text-slate-900"
                        onClick={async () => {
                          try {
                            const r = await api.get(`/rental-overview/export`, {
                              params: { owner_id: c.id, format: 'pdf' },
                              responseType: 'blob',
                            });
                            const url = URL.createObjectURL(r.data);
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 5000);
                          } catch (e) { toast.error("Vermietungsübersicht fehlgeschlagen"); }
                        }}
                        title="Vermietungsübersicht aller Objekte des Eigentümers (PDF)"
                        data-testid={`rental-overview-${c.id}`}
                      >
                        <Buildings size={14} />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700"
                      onClick={async () => {
                        if (!await window._domivusConfirm("Kontakt wirklich löschen?")) return;
                        await api.delete(`/contacts/${c.id}`);
                        toast.success("Kontakt gelöscht");
                        load();
                      }}
                      data-testid={`delete-contact-${c.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MessagingDialog
        open={!!msgContact}
        onOpenChange={(o) => { if (!o) setMsgContact(null); }}
        context={msgContact ? { contact_id: msgContact.id } : {}}
        defaultEmail={firstEmail(msgContact?.emails)}
        defaultSubject=""
      />
      <OwnerPortalDialog
        contact={portalContact}
        open={!!portalContact}
        onOpenChange={(o) => { if (!o) setPortalContact(null); }}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

export function ContactDialog({ editing, onSaved, initialRole = null, initialContactType = null }) {
  const { user } = useAuth();
  const canEditOwnerNumber = ["super_admin", "admin", "management"].includes(user?.role);
  const initial = () => editing || {
    contact_type: initialContactType || "person",
    salutation: "",
    first_name: "", last_name: "", company_name: "",
    emails: [""], phones: [""],
    contact_persons: [],
    address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    former_addresses: [],
    bank_accounts: [],
    roles: initialRole ? [initialRole] : ["tenant"], notes: "",
    address_block_mode: "auto",
    address_block_override: "",
    hide_salutation_prefix: false,
    // iter144 — Personalien
    birth_date: "",
    birth_place: "",
    nationality: "",
    id_document_type: "",
    id_document_number: "",
    id_document_issuing_authority: "",
    id_document_issued_on: "",
    id_document_valid_until: "",
    marital_status: "",
    occupation: "",
    employer: "",
  };
  const [form, setForm] = useState(initial);
  const [showAddressChange, setShowAddressChange] = useState(false);
  const [addressChangeForm, setAddressChangeForm] = useState({
    new_address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    archived_at: new Date().toISOString().slice(0, 10),
    reason: "",
  });

  useEffect(() => {
    setForm(initial());
    setShowAddressChange(false);
    // eslint-disable-next-line
  }, [editing]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...(s.address || {}), [k]: v } }));
  const setNewAddr = (k, v) => setAddressChangeForm((s) => ({ ...s, new_address: { ...s.new_address, [k]: v } }));

  // VM-13b — Briefanrede-Vorschlag aus Anrede + Nachname / Firmenname bauen.
  // Grundregel: das System muss sich blind darauf verlassen, dass ein Komma
  // am Ende steht — das ergänzt der Backend-Renderer.
  const suggestedBriefanrede = (() => {
    if (form.contact_type === "company" || form.contact_type === "institution"
        || form.contact_type === "court" || form.contact_type === "authority_office") {
      return "Sehr geehrte Damen und Herren";
    }
    const last = (form.last_name || "").trim();
    const sal = (form.salutation || "").toLowerCase();
    if (!last) return "Sehr geehrte Damen und Herren";
    if (sal === "frau") return `Sehr geehrte Frau ${last}`;
    if (sal === "herr") return `Sehr geehrter Herr ${last}`;
    // Anrede unbekannt → neutrale Ausweichvariante
    const first = (form.first_name || "").trim();
    return first ? `Guten Tag ${first} ${last}`.trim() : `Guten Tag ${last}`;
  })();
  // Sprint 5c — PLZ→Ort Auto-Fill
  const { hit: plzHitMain } = usePlzAutocomplete(form.address?.zip, (city) => setAddr("city", city), form.address?.city || "");
  const { hit: plzHitNew } = usePlzAutocomplete(addressChangeForm.new_address.zip, (city) => setNewAddr("city", city), addressChangeForm.new_address.city || "");

  const submit = async (e) => {
    e.preventDefault();
    // VM-13b — Wenn keine Briefanrede eingetragen, automatisch den Vorschlag
    // persistieren, damit sich Briefe blind darauf verlassen können.
    const briefanrede = (form.alternative_salutation || "").trim() || suggestedBriefanrede;
    const payload = {
      ...form,
      salutation: form.contact_type === "person" ? (form.salutation || null) : null,
      alternative_salutation: briefanrede,
      emails: (form.emails || []).filter((e) => (typeof e === "string" ? e : e?.email)),
      contact_persons: (form.contact_persons || []).filter((p) => p?.contact_id),
      phones: (form.phones || []).filter((p) => p),
      bank_accounts: (form.bank_accounts || []).filter((a) => (a?.iban || "").trim() !== ""),
      // iter144 — Enum-Felder: leere Strings zu null umsetzen
      id_document_type: form.id_document_type || null,
      marital_status: form.marital_status || null,
      birth_date: form.birth_date || null,
      id_document_issued_on: form.id_document_issued_on || null,
      id_document_valid_until: form.id_document_valid_until || null,
    };
    try {
      if (editing) {
        await api.patch(`/contacts/${editing.id}`, payload);
        toast.success("Kontakt aktualisiert");
      } else {
        await api.post("/contacts", payload);
        toast.success("Kontakt angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const submitAddressChange = async () => {
    if (!editing?.id) return;
    const na = addressChangeForm.new_address;
    if (!na.street && !na.city && !na.zip) {
      toast.error("Bitte mindestens Straße, PLZ oder Ort der neuen Adresse angeben");
      return;
    }
    try {
      await api.post(`/contacts/${editing.id}/address-change`, addressChangeForm);
      toast.success("Adressänderung gespeichert – alte Adresse archiviert");
      // refresh: reload contact
      const { data } = await api.get(`/contacts/${editing.id}`);
      setForm({ ...data, emails: data.emails || [""], phones: data.phones || [""], contact_persons: data.contact_persons || [] });
      setShowAddressChange(false);
      setAddressChangeForm({
        new_address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
        archived_at: new Date().toISOString().slice(0, 10),
        reason: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern der Adressänderung");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{editing ? "Kontakt bearbeiten" : "Neuer Kontakt"}</DialogTitle>
        <DialogDescription>Stammdaten erfassen. Mehrere Rollen je Kontakt möglich.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Kontakttyp">
            <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
              <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="company">Firma</SelectItem>
                <SelectItem value="institution">Institution</SelectItem>
                <SelectItem value="court">Gericht</SelectItem>
                <SelectItem value="authority_office">Behörde/Amt</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {form.contact_type === "person" ? (
            <>
              <Field label="Anrede">
                <Select value={form.salutation || ""} onValueChange={(v) => set("salutation", v)}>
                  <SelectTrigger data-testid="contact-salutation"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SALUTATIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Vorname">
                <Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="contact-firstname" />
              </Field>
              <Field label="Nachname" className="col-span-3">
                <Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="contact-lastname" />
              </Field>
            </>
          ) : (
            <Field label={
              form.contact_type === "court" ? "Bezeichnung des Gerichts"
              : form.contact_type === "authority_office" ? "Bezeichnung der Behörde"
              : form.contact_type === "institution" ? "Bezeichnung der Institution"
              : "Firmenname"
            } className="col-span-2">
              <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="contact-companyname" />
            </Field>
          )}
        </div>

        {/* iter144 — Personalien-Sektion (nur für Personen) */}
        {form.contact_type === "person" && (
          <details className="border border-slate-200 rounded-md" data-testid="contact-personalien-section">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700 bg-slate-50">
              Personalien &amp; Ausweisdaten <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </summary>
            <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Geburtsdatum">
                <Input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} data-testid="contact-birth-date" />
              </Field>
              <Field label="Geburtsort">
                <Input value={form.birth_place || ""} onChange={(e) => set("birth_place", e.target.value)} data-testid="contact-birth-place" />
              </Field>
              <Field label="Staatsangehörigkeit">
                <Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} placeholder="deutsch" data-testid="contact-nationality" />
              </Field>
              <Field label="Familienstand">
                <select value={form.marital_status || ""} onChange={(e) => set("marital_status", e.target.value)} className="h-9 w-full border border-slate-200 rounded px-2 text-sm bg-white" data-testid="contact-marital">
                  <option value="">—</option>
                  <option value="single">ledig</option>
                  <option value="married">verheiratet</option>
                  <option value="partnership">Partnerschaft</option>
                  <option value="divorced">geschieden</option>
                  <option value="separated">getrennt lebend</option>
                  <option value="widowed">verwitwet</option>
                </select>
              </Field>
              <Field label="Beruf" className="col-span-2">
                <Input value={form.occupation || ""} onChange={(e) => set("occupation", e.target.value)} data-testid="contact-occupation" />
              </Field>
              <Field label="Arbeitgeber" className="col-span-2">
                <Input value={form.employer || ""} onChange={(e) => set("employer", e.target.value)} data-testid="contact-employer" />
              </Field>
              <Field label="Ausweistyp">
                <select value={form.id_document_type || ""} onChange={(e) => set("id_document_type", e.target.value)} className="h-9 w-full border border-slate-200 rounded px-2 text-sm bg-white" data-testid="contact-id-type">
                  <option value="">—</option>
                  <option value="id_card">Personalausweis</option>
                  <option value="passport">Reisepass</option>
                  <option value="residence_permit">Aufenthaltstitel</option>
                  <option value="driving_license">Führerschein</option>
                  <option value="other">Sonstiger</option>
                </select>
              </Field>
              <Field label="Ausweisnummer">
                <Input value={form.id_document_number || ""} onChange={(e) => set("id_document_number", e.target.value)} data-testid="contact-id-number" />
              </Field>
              <Field label="Ausgestellt von">
                <Input value={form.id_document_issuing_authority || ""} onChange={(e) => set("id_document_issuing_authority", e.target.value)} data-testid="contact-id-authority" />
              </Field>
              <Field label="Ausgestellt am">
                <Input type="date" value={form.id_document_issued_on || ""} onChange={(e) => set("id_document_issued_on", e.target.value)} data-testid="contact-id-issued" />
              </Field>
              <Field label="Gültig bis">
                <Input type="date" value={form.id_document_valid_until || ""} onChange={(e) => set("id_document_valid_until", e.target.value)} data-testid="contact-id-valid-until" />
              </Field>
            </div>
          </details>
        )}

        {/* VM-13b — Briefanrede: pro Kontakt gespeichert, blind vertrauensfähig */}
        <div className="grid grid-cols-1 gap-1">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Briefanrede <span className="normal-case text-slate-400 font-normal">(wird in allen Briefen verwendet)</span>
          </label>
          <div className="flex gap-2 items-center">
            <Input
              value={form.alternative_salutation || ""}
              onChange={(e) => set("alternative_salutation", e.target.value)}
              placeholder={suggestedBriefanrede}
              data-testid="contact-alternative-salutation"
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => set("alternative_salutation", suggestedBriefanrede)}
              data-testid="contact-briefanrede-suggest"
              className="whitespace-nowrap"
            >
              Vorschlag übernehmen
            </Button>
          </div>
          <p className="text-[10px] text-slate-400">
            Vorschlag: <span className="font-mono text-slate-600">{suggestedBriefanrede}</span> — im Brief wird automatisch ein Komma angehängt. Makro: <code className="font-mono">{"{{recipient.greeting}}"}</code>.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <EmailListField
            emails={form.emails || []}
            onChange={(list) => set("emails", list)}
          />
          <PhoneListField
            phones={form.phones || []}
            onChange={(list) => set("phones", list)}
          />
        </div>

        <CompanyContactPersonsField
          persons={form.contact_persons || []}
          onChange={(list) => set("contact_persons", list)}
          contactType={form.contact_type}
        />

        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Aktuelle Adresse</div>
            {editing && (
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setShowAddressChange((s) => !s)}
                data-testid="address-change-toggle">
                {showAddressChange ? "Abbrechen" : "Adresse ändern (Umzug)"}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Field label="1. Anschriftzeile (optional)" className="col-span-4">
              <Input
                value={form.address?.address_line1 || ""}
                onChange={(e) => setAddr("address_line1", e.target.value)}
                placeholder={suggestAddressLine1(form) || "z. B. An das Amtsgericht — nur wenn benötigt"}
                list="address-line1-suggestions"
                data-testid="contact-address-line1"
              />
              <datalist id="address-line1-suggestions">
                {ADDRESS_LINE1_COMMON.map((v) => <option key={v} value={v} />)}
              </datalist>
              {!form.address?.address_line1 && suggestAddressLine1(form) && (
                <button
                  type="button"
                  onClick={() => setAddr("address_line1", suggestAddressLine1(form))}
                  className="mt-1 text-[10px] text-emerald-700 hover:underline"
                  data-testid="contact-address-line1-suggest"
                >
                  Vorschlag übernehmen: &bdquo;{suggestAddressLine1(form)}&ldquo;
                </button>
              )}
            </Field>
            <Field label="Straße" className="col-span-2">
              <Input value={form.address?.street || ""} onChange={(e) => setAddr("street", e.target.value)} data-testid="contact-street" />
            </Field>
            <Field label="Nr.">
              <Input value={form.address?.house_number || ""} onChange={(e) => setAddr("house_number", e.target.value)} />
            </Field>
            <Field label="PLZ">
              <Input value={form.address?.zip || ""} onChange={(e) => setAddr("zip", e.target.value)} maxLength={5} data-testid="contact-zip" />
            </Field>
            <Field label="Ort" className="col-span-4">
              <Input value={form.address?.city || ""} onChange={(e) => setAddr("city", e.target.value)} data-testid="contact-city" />
              <PlzAutocompleteHint hit={plzHitMain} />
            </Field>
          </div>

          {showAddressChange && editing && (
            <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 space-y-3" data-testid="address-change-form">
              <div className="text-xs font-semibold text-amber-900">Neue Adresse eingeben</div>
              <div className="text-[11px] text-amber-900/80 italic">
                Beim Speichern wird die oben angezeigte aktuelle Adresse in „Ehemalige Adressen“ archiviert und durch die hier eingegebene ersetzt.
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Straße" className="col-span-2">
                  <Input value={addressChangeForm.new_address.street} onChange={(e) => setNewAddr("street", e.target.value)} data-testid="new-address-street" />
                </Field>
                <Field label="Nr.">
                  <Input value={addressChangeForm.new_address.house_number} onChange={(e) => setNewAddr("house_number", e.target.value)} />
                </Field>
                <Field label="PLZ">
                  <Input value={addressChangeForm.new_address.zip} onChange={(e) => setNewAddr("zip", e.target.value)} maxLength={5} data-testid="new-address-zip" />
                </Field>
                <Field label="Ort" className="col-span-3">
                  <Input value={addressChangeForm.new_address.city} onChange={(e) => setNewAddr("city", e.target.value)} data-testid="new-address-city" />
                  <PlzAutocompleteHint hit={plzHitNew} />
                </Field>
                <Field label="Gültig ab">
                  <Input type="date" value={addressChangeForm.archived_at}
                    onChange={(e) => setAddressChangeForm((s) => ({ ...s, archived_at: e.target.value }))}
                    title="Tag, ab dem die neue Adresse gilt" />
                </Field>
              </div>
              <Field label="Grund (optional)">
                <Input value={addressChangeForm.reason}
                  onChange={(e) => setAddressChangeForm((s) => ({ ...s, reason: e.target.value }))}
                  placeholder="z. B. Umzug in eigene Wohnung" />
              </Field>
              <div className="flex justify-end">
                <Button type="button" size="sm" className="bg-amber-600 hover:bg-amber-700"
                  onClick={submitAddressChange} data-testid="address-change-save">
                  Adressänderung speichern
                </Button>
              </div>
            </div>
          )}

          {editing && (form.former_addresses || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Ehemalige Adressen</div>
              <ul className="space-y-1.5">
                {(form.former_addresses || []).map((fa, idx) => (
                  <li key={idx} className="text-xs bg-white border border-slate-200 rounded-sm px-2.5 py-1.5 flex items-center justify-between gap-3" data-testid={`former-address-${idx}`}>
                    <div className="min-w-0 truncate text-slate-700">
                      {[fa.street, fa.house_number].filter(Boolean).join(" ")}{fa.zip || fa.city ? `, ${[fa.zip, fa.city].filter(Boolean).join(" ")}` : ""}
                      {fa.reason && <span className="text-slate-400"> · {fa.reason}</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 shrink-0 tabular-nums">
                      {fa.archived_at ? `gültig bis ${formatDate(fa.archived_at)}` : "—"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* 2026-08 — Adressblock-Override (Anschriftfenster manuell überschreiben) */}
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid="contact-address-block-override">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 cursor-pointer"
              checked={form.address_block_mode === "manual"}
              onChange={(e) => set("address_block_mode", e.target.checked ? "manual" : "auto")}
              data-testid="address-block-mode-toggle"
            />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Adressfenster manuell überschreiben
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Standardmäßig wird der Adressblock automatisch aus Anrede + Name + Adresse generiert.
                Wenn Sie z.&nbsp;B. die Zeile &bdquo;Firma&ldquo; weglassen möchten, aktivieren Sie diese Option
                und geben den Namensteil des Adressfensters frei ein.
              </div>
            </div>
          </label>
          {form.address_block_mode === "manual" && (
            <div className="mt-3">
              <Textarea
                value={form.address_block_override || ""}
                onChange={(e) => set("address_block_override", e.target.value)}
                rows={3}
                placeholder={"Beispiel:\nMustermann GmbH & Co. KG\nKanzlei am Marktplatz"}
                className="text-sm font-mono"
                data-testid="address-block-override-text"
              />
              <div className="text-[10px] text-slate-500 mt-1.5">
                Diese Zeilen ersetzen den automatischen Namensteil im Adressfenster. Straße, PLZ und Ort
                werden weiterhin aus der Adresse oben verwendet. &bdquo;z.&nbsp;Hd. &hellip;&ldquo; wird bei aktivem Ansprechpartner ergänzt.
              </div>
            </div>
          )}
          <label className="flex items-start gap-2 mt-3 pt-3 border-t border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 cursor-pointer"
              checked={!!form.hide_salutation_prefix}
              onChange={(e) => set("hide_salutation_prefix", e.target.checked)}
              data-testid="hide-salutation-prefix-toggle"
            />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Bezeichnung weglassen (Firma / Herrn / Frau / …)
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Blendet die Anrede-Bezeichnung im Adressfenster aus. So erscheint z.&nbsp;B. nur
                der Firmenname ohne vorangestelltes &bdquo;Firma&ldquo;, oder der Name einer
                Person ohne &bdquo;Herrn/Frau&ldquo;.
              </div>
            </div>
          </label>
        </div>

        <Field label="Rollen">
          <div className="flex flex-wrap gap-2">
            {ROLE_OPTIONS.map(([k, v]) => {
              const active = (form.roles || []).includes(k);
              return (
                <button
                  type="button" key={k}
                  onClick={() => set("roles", active ? form.roles.filter((r) => r !== k) : [...(form.roles || []), k])}
                  className={`px-2.5 py-1 rounded-sm text-xs border transition-colors ${
                    active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`contact-role-${k}`}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </Field>

        {editing && (form.roles || []).includes("owner") && canEditOwnerNumber && (
          <OwnerNumberEditor
            contactId={editing.id}
            current={form.owner_number || editing.owner_number || ""}
            onChanged={(newNum) => { set("owner_number", newNum || ""); }}
          />
        )}

        {/* 2026-03 — IBAN-Autolearn: Vorschläge aus Bank-Match anzeigen */}
        {editing?.id && (
          <IbanSuggestions
            contactId={editing.id}
            currentIban={form.iban}
            onAccept={(iban) => set("iban", iban)}
          />
        )}

        {/* Sprint 5g — Bankverbindungen (v. a. für Eigentümer, dienen als Mietkonten in MV-Anlage) */}
        <BankAccountsEditor
          accounts={form.bank_accounts || []}
          onChange={(next) => set("bank_accounts", next)}
        />

        <Field label="Notizen">
          <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
        </Field>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="contact-save-btn">
            {editing ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-700 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "border-slate-900 text-slate-900 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

/* iter138 — Multi-VCF-Upload für Kontakte */
function VcfImportButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/contacts/import/vcf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const sum = data.summary || {};
      const created = sum.created_count || 0;
      const skipped = sum.skipped_count || 0;
      const errors = sum.error_count || 0;
      if (created > 0) toast.success(`${created} Kontakt(e) importiert${skipped ? ` · ${skipped} übersprungen` : ""}${errors ? ` · ${errors} Fehler` : ""}`);
      else if (skipped > 0 && errors === 0) toast.info(`Alle ${skipped} Kontakte existieren bereits — nichts importiert`);
      else if (errors > 0) toast.error(`Import mit ${errors} Fehlern beendet`);
      else toast.warning("Keine gültigen vCards in den ausgewählten Dateien gefunden");
      onDone?.();
    } catch (err) {
      toast.error("Import fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".vcf,text/vcard,text/x-vcard"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid="vcf-import-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={pick}
        disabled={busy}
        className="gap-2"
        data-testid="vcf-import-btn"
        title="VCF-Datei(en) importieren — mehrere Dateien möglich"
      >
        <UploadSimple size={16} /> {busy ? "Importiere…" : "VCF importieren"}
      </Button>
      {result && (result.errors?.length > 0 || result.skipped?.length > 0) && (
        <div className="hidden" data-testid="vcf-import-result" data-created={result.summary?.created_count || 0} data-skipped={result.summary?.skipped_count || 0} data-errors={result.summary?.error_count || 0} />
      )}
    </>
  );
}



function OwnerNumberEditor({ contactId, current, onChanged }) {
  const [value, setValue] = useState(current || "");
  const [check, setCheck] = useState({ state: "idle", message: "", used_by: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(current || ""); }, [current]);

  // Echtzeit-Eindeutigkeitsprüfung (debounced)
  useEffect(() => {
    const v = (value || "").trim();
    if (!v) { setCheck({ state: "idle", message: "", used_by: null }); return; }
    if (v === (current || "").trim()) { setCheck({ state: "idle", message: "", used_by: null }); return; }
    setCheck({ state: "checking", message: "Prüfe…", used_by: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/owner-number/check", {
          params: { value: v, exclude_id: contactId },
        });
        if (data.available) {
          setCheck({ state: "available", message: `Verfügbar: ${data.normalized}`, used_by: null });
        } else {
          setCheck({
            state: "taken",
            message: `Wird bereits verwendet${data.used_by?.name ? ` (${data.used_by.name})` : ""}`,
            used_by: data.used_by || null,
          });
        }
      } catch {
        setCheck({ state: "idle", message: "", used_by: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value, current, contactId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contacts/${contactId}/owner-number`, {
        owner_number: value,
      });
      toast.success(`Eigentümernummer ${data.owner_number ? `auf ${data.owner_number}` : "entfernt"} aktualisiert`);
      onChanged?.(data.owner_number);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const changed = (value || "").trim() !== (current || "").trim();
  const disabled = !changed || check.state === "taken" || check.state === "checking" || saving;

  return (
    <div
      className="border border-amber-200 bg-amber-50/60 rounded-md p-3 space-y-2"
      data-testid="owner-number-editor"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-900">Eigentümernummer</div>
          <div className="text-[11px] text-amber-900/70 mt-0.5">
            Nur Admins. 3-stellig, eindeutig pro Mandant. Wird automatisch mit führenden Nullen normalisiert.
          </div>
        </div>
        <span className="text-[10px] font-mono px-2 py-1 bg-white border border-amber-200 rounded text-amber-900">
          aktuell: {current || "—"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="z. B. 042"
          className="font-mono h-9 w-32"
          maxLength={4}
          data-testid="owner-number-input"
        />
        <Button
          type="button"
          size="sm"
          className="bg-amber-700 hover:bg-amber-800 h-9"
          onClick={save}
          disabled={disabled}
          data-testid="owner-number-save"
        >
          {saving ? "Speichere…" : "Übernehmen"}
        </Button>
        {check.state === "checking" && (
          <span className="text-xs text-slate-500" data-testid="owner-number-checking">Prüfe…</span>
        )}
        {check.state === "available" && (
          <span className="text-xs text-emerald-700 font-medium" data-testid="owner-number-available">
            ✓ {check.message}
          </span>
        )}
        {check.state === "taken" && (
          <span className="text-xs text-rose-700 font-medium" data-testid="owner-number-taken">
            ⚠ {check.message}
          </span>
        )}
      </div>
    </div>
  );
}




// 2026-03 — IBAN-Vorschläge aus Bank-Autolearn
function IbanSuggestions({ contactId, currentIban, onAccept }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyIban, setBusyIban] = useState(null);

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contactId}/iban-suggestions`);
      setSuggestions(data.suggestions || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  if (!suggestions.length) return null;

  const accept = async (iban) => {
    setBusyIban(iban);
    try {
      await api.post(`/contacts/${contactId}/iban-suggestions/${encodeURIComponent(iban)}/accept`);
      toast.success("IBAN als Standard übernommen");
      onAccept?.(iban);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally { setBusyIban(null); }
  };
  const dismiss = async (iban) => {
    setBusyIban(iban);
    try {
      await api.post(`/contacts/${contactId}/iban-suggestions/${encodeURIComponent(iban)}/dismiss`);
      toast.info("Vorschlag verworfen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen");
    } finally { setBusyIban(null); }
  };
  const fmtIban = (v) => (v || "").replace(/(.{4})/g, "$1 ").trim();
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-2"
         data-testid="iban-suggestions">
      <div className="flex items-center gap-2 text-emerald-900">
        <span className="text-xs font-semibold uppercase tracking-wider">IBAN-Vorschläge</span>
        <span className="text-[11px] text-emerald-700">
          aus Bank-Auto-Match (Namensübereinstimmung + Mietzahlung erkannt)
        </span>
      </div>
      {suggestions.map((s) => (
        <div key={s.iban}
             className="flex items-center gap-2 bg-white border border-emerald-300 rounded p-2"
             data-testid={`iban-sug-${s.iban}`}>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-sm text-slate-900 truncate">{fmtIban(s.iban)}</div>
            <div className="text-[11px] text-slate-500 truncate">
              {s.count > 1 ? `${s.count}× erkannt · ` : ""}Absender: {s.source_name || "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => accept(s.iban)}
            disabled={busyIban === s.iban}
            className="px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
            data-testid={`iban-accept-${s.iban}`}
          >
            als Standard übernehmen
          </button>
          <button
            type="button"
            onClick={() => dismiss(s.iban)}
            disabled={busyIban === s.iban}
            className="px-2 py-1 text-xs bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded disabled:opacity-50"
            data-testid={`iban-dismiss-${s.iban}`}
          >
            Verwerfen
          </button>
        </div>
      ))}
    </div>
  );
}



// Sprint 5g — Bankverbindungen: mehrere IBANs pro Kontakt (v. a. Eigentümer)
function BankAccountsEditor({ accounts, onChange }) {
  const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
  // 2026-02 — IBAN-Auto-Erkennung: bei jedem Konto merken wir den letzten
  // Lookup, damit Warnungen (BIC/Bank weicht ab) korrekt gerendert werden.
  const [ibanInfo, setIbanInfo] = useState({}); // { [idx]: { bic, bank_name, valid, note, ... } }
  const [ibanLoading, setIbanLoading] = useState({});
  const [ibanTouched, setIbanTouched] = useState({}); // { [idx]: { bic: true, bank_name: true } }

  // 2026-02-24 · Fix „letzte IBAN-Ziffer verschwindet": Async-Lookup darf den
  // aktuellen State nicht aus einer stale Closure überschreiben. Wir halten
  // eine Ref auf den aktuellen `accounts`-Array und nutzen sie im Async-Pfad.
  const accountsRef = useRef(accounts);
  useEffect(() => { accountsRef.current = accounts; }, [accounts]);

  const add = () => {
    onChange([
      ...accountsRef.current,
      { id: uid(), label: "", account_holder: "", bank_name: "", iban: "", bic: "", purpose: "rent", is_default: accountsRef.current.length === 0 },
    ]);
  };
  const update = (idx, patch) => {
    const src = accountsRef.current;
    const next = src.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    accountsRef.current = next;
    onChange(next);
  };
  const remove = (idx) => {
    const src = accountsRef.current;
    const next = src.filter((_, i) => i !== idx);
    if (src[idx]?.is_default && next.length > 0 && !next.some((a) => a.is_default)) {
      next[0] = { ...next[0], is_default: true };
    }
    accountsRef.current = next;
    onChange(next);
    // Aufräumen
    setIbanInfo((s) => { const n = { ...s }; delete n[idx]; return n; });
    setIbanTouched((s) => { const n = { ...s }; delete n[idx]; return n; });
  };
  const setDefault = (idx) => {
    const next = accountsRef.current.map((a, i) => ({ ...a, is_default: i === idx }));
    accountsRef.current = next;
    onChange(next);
  };
  const formatIban = (v) => (v || "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();

  // 2026-02 — IBAN-Lookup: nach Blur oder wenn min. 15 Zeichen und noch kein BIC gesetzt
  const runIbanLookup = async (idx, ibanRaw) => {
    const raw = (ibanRaw || "").replace(/\s+/g, "").toUpperCase();
    if (raw.length < 8) return;
    setIbanLoading((s) => ({ ...s, [idx]: true }));
    try {
      const { data } = await api.get(`/iban/lookup?iban=${encodeURIComponent(raw)}`);
      setIbanInfo((s) => ({ ...s, [idx]: data }));
      if (data?.valid) {
        // Auto-Fill nur, wenn User nichts eingegeben oder Feld leer ist.
        // Wichtig: aktuellen Ref-Stand nutzen, damit inzwischen getippte
        // IBAN-Ziffern nicht durch die alte Closure überschrieben werden.
        const touched = ibanTouched[idx] || {};
        const cur = accountsRef.current[idx] || {};
        const patch = {};
        if (data.bic && (!cur.bic || !touched.bic)) patch.bic = data.bic;
        if (data.bank_name && (!cur.bank_name || !touched.bank_name)) patch.bank_name = data.bank_name;
        if (Object.keys(patch).length > 0) update(idx, patch);
      }
    } catch { /* Netz-Fehler → still */ }
    finally { setIbanLoading((s) => ({ ...s, [idx]: false })); }
  };

  const bicMismatch = (idx) => {
    const info = ibanInfo[idx];
    const cur = accounts[idx];
    if (!info?.valid || !info.bic || !cur?.bic) return null;
    const a = cur.bic.replace(/\s+/g, "").toUpperCase();
    const b = info.bic.replace(/\s+/g, "").toUpperCase();
    // BIC vergleicht 8-Kern-Zeichen (Filiale XXX ist optional)
    return a.slice(0, 8) !== b.slice(0, 8) ? info.bic : null;
  };
  const bankMismatch = (idx) => {
    const info = ibanInfo[idx];
    const cur = accounts[idx];
    if (!info?.valid || !info.bank_name || !cur?.bank_name) return null;
    const norm = (s) => s.toLowerCase().replace(/[^a-zäöüß0-9]/gi, "");
    return !norm(cur.bank_name).includes(norm(info.bank_name).slice(0, 6))
      && !norm(info.bank_name).includes(norm(cur.bank_name).slice(0, 6))
      ? info.bank_name : null;
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="bank-accounts-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">Bankverbindungen</div>
          <div className="text-[11px] text-slate-500">Bei Eigentümer-Kontakten wählbar als Mietkonto in der Anlage eines Mietverhältnisses.</div>
        </div>
        <button
          type="button"
          onClick={add}
          className="text-xs bg-slate-900 text-white px-2.5 py-1 rounded hover:bg-slate-800"
          data-testid="bank-account-add"
        >
          + Konto hinzufügen
        </button>
      </div>
      {accounts.length === 0 ? (
        <div className="text-xs text-slate-400 italic px-1 py-3">Noch keine Bankverbindung hinterlegt.</div>
      ) : (
        <div className="space-y-2">
          {accounts.map((a, idx) => (
            <div key={a.id || idx} className="border border-slate-200 bg-white rounded-md p-3 space-y-2.5" data-testid={`bank-account-row-${idx}`}>
              {/* Zeile 1 — IBAN + BIC */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500 flex items-center gap-1">
                    IBAN *
                    {ibanLoading[idx] && <span className="text-emerald-700 italic">· prüfe…</span>}
                    {ibanInfo[idx]?.valid && !ibanLoading[idx] && (
                      <span className="text-emerald-700 italic">· {ibanInfo[idx].country_code}{ibanInfo[idx].is_sepa ? " · SEPA" : ""}</span>
                    )}
                    {ibanInfo[idx] && ibanInfo[idx].valid === false && (
                      <span className="text-rose-700 italic">· ungültig</span>
                    )}
                  </Label>
                  <Input
                    className={`h-9 text-sm font-mono ${ibanInfo[idx] && ibanInfo[idx].valid === false ? "border-rose-400" : ""}`}
                    value={formatIban(a.iban)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\s+/g, "").toUpperCase();
                      update(idx, { iban: raw });
                      if (raw.length >= 15) runIbanLookup(idx, raw);
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value.replace(/\s+/g, "").toUpperCase();
                      if (raw.length >= 8) runIbanLookup(idx, raw);
                    }}
                    placeholder="DE12 3456 7890 1234 5678 90"
                    data-testid={`bank-account-iban-${idx}`}
                  />
                  {ibanInfo[idx]?.note && (
                    <div className={`text-[10px] mt-0.5 ${ibanInfo[idx].valid ? "text-amber-700" : "text-rose-700"}`}>
                      {ibanInfo[idx].note}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">BIC</Label>
                  <Input
                    className={`h-9 text-sm font-mono ${bicMismatch(idx) ? "border-amber-400 bg-amber-50" : ""}`}
                    value={a.bic || ""}
                    onChange={(e) => {
                      setIbanTouched((s) => ({ ...s, [idx]: { ...(s[idx] || {}), bic: true } }));
                      update(idx, { bic: e.target.value.toUpperCase() });
                    }}
                    placeholder="COKSDE33XXX"
                    data-testid={`bank-account-bic-${idx}`}
                  />
                  {bicMismatch(idx) && (
                    <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`bank-account-bic-mismatch-${idx}`}>
                      Weicht ab (erwartet: {bicMismatch(idx)}).{" "}
                      <button type="button" className="underline hover:text-amber-900"
                              onClick={() => { update(idx, { bic: bicMismatch(idx) }); setIbanTouched((s) => ({ ...s, [idx]: { ...(s[idx] || {}), bic: false } })); }}>Übernehmen</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Zeile 2 — Bezeichnung, Kontoinhaber, Bank */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Bezeichnung</Label>
                  <Input className="h-9 text-sm" value={a.label || ""}
                         onChange={(e) => update(idx, { label: e.target.value })}
                         placeholder="z. B. Mietkonto Objekt B-01"
                         data-testid={`bank-account-label-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Kontoinhaber</Label>
                  <Input className="h-9 text-sm" value={a.account_holder || ""}
                         onChange={(e) => update(idx, { account_holder: e.target.value })}
                         placeholder="Name laut Bank" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Bank</Label>
                  <Input className={`h-9 text-sm ${bankMismatch(idx) ? "border-amber-400 bg-amber-50" : ""}`}
                         value={a.bank_name || ""}
                         onChange={(e) => {
                           setIbanTouched((s) => ({ ...s, [idx]: { ...(s[idx] || {}), bank_name: true } }));
                           update(idx, { bank_name: e.target.value });
                         }}
                         placeholder="z. B. Sparkasse KölnBonn"
                         data-testid={`bank-account-bank-${idx}`} />
                  {bankMismatch(idx) && (
                    <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`bank-account-bank-mismatch-${idx}`}>
                      Weicht ab (erwartet: {bankMismatch(idx)}).{" "}
                      <button type="button" className="underline hover:text-amber-900"
                              onClick={() => { update(idx, { bank_name: bankMismatch(idx) }); setIbanTouched((s) => ({ ...s, [idx]: { ...(s[idx] || {}), bank_name: false } })); }}>Übernehmen</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Zeile 3 — Verwendung + Standard + Entfernen */}
              <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-slate-100">
                <div className="w-full sm:w-56">
                  <Label className="text-[10px] uppercase text-slate-500">Verwendung</Label>
                  <select className="h-9 w-full text-sm border border-slate-200 rounded px-2 bg-white"
                          value={a.purpose || "rent"}
                          onChange={(e) => update(idx, { purpose: e.target.value })}>
                    <option value="rent">Mietkonto</option>
                    <option value="deposit">Kautionskonto</option>
                    <option value="general">Allgemein</option>
                  </select>
                </div>
                <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer pb-2">
                  <input type="checkbox" checked={!!a.is_default}
                         onChange={() => setDefault(idx)}
                         className="h-4 w-4 accent-slate-900"
                         data-testid={`bank-account-default-${idx}`} />
                  Standard-Konto
                </label>
                <button type="button" onClick={() => remove(idx)}
                        className="ml-auto text-xs text-rose-700 hover:text-rose-900 pb-2"
                        data-testid={`bank-account-remove-${idx}`}>
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
